/**
 * A component that lists all of a student's assignments.
 **/
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";
import {useCallback, useEffect, useMemo, useReducer, useRef} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import type {Result} from "@khanacademy/wonder-blocks-data";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingLarge} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {
    PageloadMarker,
    type TrackedPageSlug,
} from "@khan/analytics-performance";
import type {StudentAssignment} from "@khan/assignments-types";
import {getCurrentMinute} from "@khan/assignments-utils";
import {useQuery} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedStrut from "#components/layout-package/deprecated-strut.tsx";
import SegmentedControls from "#components/segmented-controls-package/segmented-controls.tsx";
import {moduleLayout} from "#modules-package/styles.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedSpinner from "#shared-components-package/deprecated-spinner.tsx";

import StudentAssignmentsTable from "./student-assignments-table.tsx";

const PAGE_SIZE = 10;

type OwnProps = {
    pageloadMarkerName?: TrackedPageSlug;
    classroomDescriptor?: string;
    hasActiveAssignments: boolean;
    loadingHasActiveAssignments: boolean;
    refetchHasActiveAssignments: () => void;
    onSufficientlyUsable?: () => void;
};

type Props = OwnProps;

type FilterType = "active" | "past";

type State = {
    currentCursor: string | null | undefined;
    previousCursors: Array<string | null | undefined>;
    filterId: FilterType;
};

const INITIAL_STATE: State = {
    currentCursor: null,
    previousCursors: [],
    filterId: "active",
};

const reducer = (state: State, newState: Partial<State>): State => {
    return {
        ...state,
        ...newState,
    };
};

const UNIQUE_IDS = {
    headerId: `assignments-header`,
    name: `assignments-filters`,
    containerId: `assignments-container`,
} as const;

const getDataFromQueryResult = (
    queryResult: Result<UserAssignmentsData>,
): {
    assignments: Array<StudentAssignment>;
    kaid: string;
    nextCursor: null | string;
    canUseLearnerActivities: boolean;
} => {
    // Safely extract the important data from the query result
    // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<UserAssignmentsData>'.
    const user = queryResult.data?.user;
    const assignmentsPage = user?.assignmentsPage ?? {};
    const kaid = user?.kaid ?? "";
    const canUseLearnerActivities =
        !!user?.isAIGuideEnabled && user?.hasAccessToAIGuideLearner;

    const assignments = assignmentsPage.assignments ?? [];
    const nextCursor = assignmentsPage.pageInfo?.nextCursor ?? null;
    return {
        kaid,
        assignments,
        nextCursor,
        canUseLearnerActivities,
    };
};

const StudentAssignments = (props: Props): React.ReactElement => {
    type Action = Partial<State>;
    const [state, dispatch] = useReducer<React.Reducer<State, Action>>(
        reducer,
        INITIAL_STATE,
    );

    const {
        classroomDescriptor,
        hasActiveAssignments,
        loadingHasActiveAssignments,
        onSufficientlyUsable,
        pageloadMarkerName,
        refetchHasActiveAssignments,
    } = props;
    const {currentCursor, filterId, previousCursors} = state;

    const classroomDescriptorRef = useRef(classroomDescriptor);

    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    useEffect(() => {
        publishAnalyticsEvent({
            eventType: "LoggedInHomepageClassDetailsTabClicked",
            eventSchemaVersion: 1,
            activeOrPastSubTab: filterId === "active" ? "ACTIVE" : "PAST",
            classDetailsSection: "CONTENT_ASSIGNMENTS",
        });
    }, [filterId, publishAnalyticsEvent]);

    const onFilterChange = useCallback(
        (filterId: string) => {
            // If we don't currently have any active assignments, refetch the
            // homepage query to check if anything has been assigned.
            if (!hasActiveAssignments) {
                refetchHasActiveAssignments();
            }

            // Clear the current cursor and previous cursor stack.  Changing
            // filters means we want to start at the beginning of the
            // Active/Past items
            dispatch({
                currentCursor: null,
                previousCursors: [],
                filterId: filterId as FilterType,
            });
        },
        [hasActiveAssignments, refetchHasActiveAssignments],
    );

    const handleNext = useCallback(
        (cursor?: string | null) => () => {
            // Update the cursor for the query and push the cursor on the
            // stack for navigating previous
            dispatch({
                currentCursor: cursor,
                previousCursors: [...previousCursors, cursor],
            });
        },
        [previousCursors],
    );

    const handlePrevious = useCallback(
        (isLoading: boolean) => () => {
            if (isLoading) {
                return;
            }

            // Take the top cursor of the stack.  It is the cursor for the next
            // item
            const newPreviousCursors = previousCursors.slice(0, -1);

            // Get the last item on the stack
            const currentCursor =
                newPreviousCursors.length > 0
                    ? newPreviousCursors[newPreviousCursors.length - 1]
                    : null;

            dispatch({
                currentCursor,
                previousCursors: newPreviousCursors,
            });
        },
        [previousCursors],
    );

    const queryVariables = useMemo((): QueryVariables => {
        const dueDateFilter = getCurrentMinute().toISOString();

        // When there's no filters or filter is active, we don't want to update
        // the default query variables besides the cursor.
        const result = {
            after: currentCursor,
            pageSize: PAGE_SIZE,
            classroomDescriptor,
        } as const;

        if (filterId === "active") {
            return {
                ...result,
                dueAfter: dueDateFilter,
                orderBy: "DUE_DATE_ASC",
                dueBefore: null,
            };
        }

        return {
            ...result,
            orderBy: "DUE_DATE_DESC",
            dueAfter: null,
            dueBefore: dueDateFilter,
        };
    }, [classroomDescriptor, currentCursor, filterId]);

    const skipQuery =
        loadingHasActiveAssignments ||
        (hasActiveAssignments === false && filterId === "active");
    const [queryResult, refetch] = useQuery(ASSIGNMENTS_PAGE_QUERY, {
        variables: queryVariables,
        skip: skipQuery,
    });
    const queryStatus = skipQuery ? "skipped" : queryResult.status;

    const isQueryCompletedRef = useRef(false);
    useEffect(() => {
        if (classroomDescriptor !== classroomDescriptorRef.current) {
            // Reset the filter state when coach or class changes
            dispatch({filterId: "active"});
            isQueryCompletedRef.current = false;
        }

        // Keep refs in sync with props/state
        classroomDescriptorRef.current = classroomDescriptor;
    }, [classroomDescriptor, filterId]);

    useEffect(() => {
        // Prevent infinite render loop
        if (isQueryCompletedRef.current) {
            return;
        }

        // Call onSufficientlyUsable when the query completes
        if (["success", "skipped"].includes(queryStatus)) {
            onSufficientlyUsable?.();
            isQueryCompletedRef.current = true;
        }

        // NOTE (emilyjanzer): This deps array isn't actually doing anything
        // right now because onSufficientlyUsable is re-created on every render,
        // so this effect will always fire. In the future we should stabilize it
        // with useCallback, but that will have to wait until all of the
        // surrounding code is converted to functional components + hooks.
    }, [onSufficientlyUsable, queryStatus]);

    const {assignments, kaid, nextCursor, canUseLearnerActivities} =
        getDataFromQueryResult(queryResult);

    const {name, containerId, headerId} = UNIQUE_IDS;
    const showPager = nextCursor || previousCursors.length > 0;

    const isLoading =
        loadingHasActiveAssignments ||
        queryStatus === "loading" ||
        queryStatus === "no-data";

    return (
        <div className={css(styles.module)}>
            <View style={styles.headingContainer}>
                <HeadingLarge tag="h3" style={styles.heading} id={headerId}>
                    {t`My assignments`}
                </HeadingLarge>
                <SegmentedControls
                    ariaControlsId={containerId}
                    ariaLabelledby={headerId}
                    value={filterId}
                    onChange={onFilterChange}
                    controls={[
                        {
                            name,
                            value: "active",
                            text: t`Active`,
                        },
                        {
                            name,
                            value: "past",
                            text: t`Past`,
                        },
                    ]}
                />
            </View>
            <View id={containerId}>
                <StudentAssignmentsTable
                    filterId={filterId}
                    isLoading={isLoading}
                    assignments={assignments}
                    canUseLearnerActivities={canUseLearnerActivities}
                    studentKaid={kaid}
                    /* handleRefetchCurrentPage happens when the user
                           opens an assignment modal and then closes it.
                           The table is asking us to re-fetch data because
                           the state of the line in the table needs to be
                           updated.
                        */
                    handleRefetchCurrentPage={refetch}
                />
                {showPager && (
                    <div className={css(styles.btnContainer)}>
                        <DeprecatedStrut size={spacing.medium_16} />
                        <Button
                            kind="tertiary"
                            aria-label={t`See previous assignments page`}
                            onClick={handlePrevious(isLoading)}
                            disabled={previousCursors.length === 0}
                        >
                            {t`Previous`}
                        </Button>
                        <DeprecatedStrut size={spacing.medium_16} />
                        |
                        <DeprecatedStrut size={spacing.medium_16} />
                        <Button
                            kind="tertiary"
                            aria-label={t`See next assignments page`}
                            onClick={handleNext(nextCursor)}
                            disabled={!nextCursor}
                        >
                            {t`Next`}
                        </Button>
                        <DeprecatedStrut size={spacing.medium_16} />
                        {isLoading && <DeprecatedSpinner size="xsmall" />}
                    </div>
                )}

                {pageloadMarkerName && (
                    <PageloadMarker
                        pageName={pageloadMarkerName}
                        sufficientlyUsable={!isLoading}
                        fullyInteractive={!isLoading}
                    />
                )}
            </View>
        </div>
    );
};

const styles = StyleSheet.create({
    btnContainer: {
        display: "flex",
        alignItems: "center",
    },
    module: {
        ...moduleLayout.defaultAlignment,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 8,
        // The table adds indention that we don't want in the LIHP.
        // Shift it back
        margin: "0 -10px 0 -8px",
    },
    headingContainer: {
        marginLeft: 12,
    },
    heading: {
        marginBottom: 24,
    },
});

type QueryVariables = {
    after: string | null | undefined;
    pageSize: number;
    classroomDescriptor: string | null | undefined;
    dueAfter: string | null | undefined;
    dueBefore: string | null | undefined;
    orderBy: "DUE_DATE_ASC" | "DUE_DATE_DESC";
};

type UserAssignmentsData = {
    user?: {
        kaid: string;
        assignmentsPage?: {
            assignments: Array<StudentAssignment>;
            pageInfo: {
                nextCursor: string | null | undefined;
            };
        };
    };
};

// TODO(classroom-goliath, CLASS-XXXX): Clean up FE to no longer refer
// to the term `totalCompletionStates` since it is now just a temporary
// alias for the field `itemCompletionStates` and the field and resolver
// will be removed as part of work documented in CLASS-5040.
// NOTE: for historical reasons the `ContentFields` fragment fetches *both*
// `id` *and* `contentId: id`. This is a bit odd, and it gets close to
// triggering a `gqlgen` bug - if we reversed the order of these fields (with
// `id` coming first) then the `LearnableContent` data doesn't get returned
// from the `assignments` service!
export const ASSIGNMENTS_PAGE_QUERY: GqlOperation<
    UserAssignmentsData,
    QueryVariables
> = gqlOp(gql`
    query UserAssignments(
        $after: ID
        $dueAfter: DateTime
        $dueBefore: DateTime
        $pageSize: Int
        $orderBy: AssignmentOrder!
        $classroomDescriptor: String
    ) {
        user {
            id
            hasAccessToAIGuideLearner
            isAIGuideEnabled
            kaid
            assignmentsPage(
                after: $after
                dueAfter: $dueAfter
                dueBefore: $dueBefore
                pageSize: $pageSize
                orderBy: $orderBy
                classroomDescriptor: $classroomDescriptor
            ) {
                assignments {
                    id
                    key
                    subjectSlug
                    title
                    contents {
                        ...ContentFields
                    }
                    assignedDate
                    dueDate
                    studentList {
                        id
                        cacheId
                        signupCode
                        name
                    }
                    totalCompletionStates: itemCompletionStates {
                        student {
                            id
                            kaid
                        }
                        state
                        completedOn
                        bestScore {
                            numCorrect
                            numAttempted
                        }
                        essaySession {
                            id
                        }
                    }
                    exerciseConfig {
                        itemPickerStrategy
                    }
                    domainId
                    courseId
                    unitId
                    lessonId
                }
                pageInfo {
                    nextCursor
                }
            }
        }
    }

    fragment ContentFields on LearnableContent {
        contentId: id
        id
        kind
        title: translatedTitle
        defaultUrlPath
        topicPaths {
            path {
                id
                kind
                slug
            }
        }
        description: translatedDescription
        thumbnailUrl
        slug
        ... on Exercise {
            expectedDoNCount: exerciseLength
            timeEstimate {
                lowerBound
                upperBound
            }
        }
        ... on Video {
            youtubeId
            translatedYoutubeId
            duration
        }
    }
`);

export default StudentAssignments;
